import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { Suspense } from "react";
import { z } from "zod";
import { PROJECT } from "@/app/capabilities/permissions";
import {
  catalogUserId,
  toolCatalogQueryOptions,
} from "@/app/tools/api/get-tool-catalog";
import { ToolsListSkeleton } from "@/app/tools/components/tools-list-skeleton";
import { ToolsList } from "@/app/tools/list";
import { RouteErrorBoundary } from "@/components/custom/router/route-error-boundary";
import { sessionQueryOptions } from "@/lib/auth-client";
import { requireProjectPermission } from "@/lib/route-guards";

const toolsSearchSchema = z.object({
  search: z.string().catch("").default(""),
  toolkits: z.array(z.string()).catch([]).default([]),
  authProviders: z.array(z.string()).catch([]).default([]),
  secrets: z.array(z.string()).catch([]).default([]),
});

const defaultValues = {
  search: "",
  // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
  toolkits: [] as string[],
  // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
  authProviders: [] as string[],
  // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
  secrets: [] as string[],
};

export const Route = createFileRoute(
  "/_authenticated/orgs/$organizationId/projects/$projectId/tools"
)({
  validateSearch: toolsSearchSchema,
  search: {
    middlewares: [stripSearchParams(defaultValues)],
  },
  errorComponent: RouteErrorBoundary,
  component: () => (
    <Suspense fallback={<ToolsListSkeleton />}>
      <ToolsList />
    </Suspense>
  ),
  beforeLoad: (ctx) => requireProjectPermission(ctx, PROJECT.tools.read),
  loader: async ({ context: { queryClient }, params }) => {
    // Resolve the session so the prefetch key matches the page's query (which
    // passes the user's email as user_id for requirement readiness).
    const session = await queryClient.ensureQueryData(sessionQueryOptions);
    queryClient.prefetchQuery(
      toolCatalogQueryOptions(params, catalogUserId(session))
    );
    return { crumb: "Tool Catalog" };
  },
});
